import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { UserModel, LoginModel, SessionModel, ResponseModel, ResetPasswordModel, ChangePasswordModel, VerificationTokenModel } from '../../models';
import { BaseService } from '../../utils/base.service';
import { AdminUserModel } from '../../models/admin_user.model';
import { ServiceTokenService } from './service-token.service';
import { Constants } from '../../constants';
import { clearListStates } from '../../utils/list-state.util';


//#region Injector
@Injectable({
  providedIn: 'root'
})
//#endregion

//#region Auth Service
export class AuthService {

  private currentUserSubject: BehaviorSubject<UserModel>;
  public currentUser: Observable<UserModel>;

  /**
   * Access token returned by the first leg of the login, kept in MEMORY ONLY (never localStorage, so
   * it stays out of reach of XSS and dies with the tab). The two-factor endpoints normally read the
   * token from the HttpOnly cookie issued at login; this is the server's documented `access_token`
   * fallback, so the verification screen still completes if the browser never stored that cookie
   * (blocked cookies, an API host that differs from the login host, a stale cookie cleared between
   * the two legs). Discarded as soon as verification succeeds.
   */
  private pendingVerificationToken: string | null = null;

  /**
   * Routes of the auth module: reachable without a session, and therefore the routes on which a
   * session teardown must never navigate (that is what turned one expiry into a redirect loop).
   */
  private static readonly PUBLIC_AUTH_PATHS = [
    '/login', '/forgot-password', '/verify', '/reset-password', '/success-forgot-password'
  ];

  /**
   * Latches {@link endSession} from the moment a teardown starts until the /login navigation has
   * settled, so a burst of parallel 401s performs exactly ONE teardown and ONE redirect.
   */
  private endingSession = false;

  //#region  Constructor
  constructor(private baseService: BaseService, private serviceTokenService: ServiceTokenService, private router: Router) {
    // Auth state is held in memory only and rebuilt from /me on bootstrap; nothing is read from
    // localStorage (the access token now lives in a Secure; HttpOnly cookie).
    this.currentUserSubject = new BehaviorSubject<UserModel>(null as any);
    this.currentUser = this.currentUserSubject.asObservable();
  }
  //#endregion

  //#region public methods

  /**
   * App bootstrap: purge any legacy localStorage access token (one-time cleanup) and rebuild auth
   * state from /me (the token is now an HttpOnly cookie the browser sends automatically), then fetch
   * the in-memory service token used for cross-host (Node/AI) calls. Always resolves.
   */
  init(): Observable<any> {
    // One-time purge of any legacy auth data left in localStorage by the pre-cookie implementation.
    localStorage.removeItem('token');
    localStorage.removeItem('admin_user');

    // Silent session probe: pass skipLoader + skipError so a 401 for a logged-out user does NOT
    // trigger the global "session expired" toast — being unauthenticated on boot is a normal state.
    return this.baseService.get('auth/me', undefined, true, true).pipe(
      map((response: HttpResponse<any>) => response.body.data),
      tap((user: any) => this.setAdminDetail(user)),
      switchMap(() => this.serviceTokenService.refresh()),
      catchError(() => {
        // No valid session: clear in-memory state so the guard redirects to login.
        this.clearToken();
        return of(null);
      })
    );
  }

  /**
   * Fetch the in-memory service token used to authenticate cross-host (Node/AI) requests.
   */
  refreshServiceToken(): Observable<any> {
    return this.serviceTokenService.refresh();
  }

  public isUserAuthenticated(): boolean {
    // In-memory auth state, rebuilt from the HttpOnly-cookie session via /me on bootstrap.
    return this.currentUserSubject.value != null;
  }

  public get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  /**
   * In-memory admin user profile for UI consumers. Returns an empty object when logged out so
   * callers that expect an object (previously `JSON.parse(localStorage.getItem('admin_user')||'{}')`)
   * keep working. This is the single source of truth — the profile is never stored in localStorage.
   */
  public getAdminUser(): any {
    return this.currentUserSubject.value ?? {};
  }

  login(requestModel: LoginModel): Observable<SessionModel> {
    return this.baseService.login('auth/session', requestModel)
      // UNWRAP THE ENVELOPE. The API answers `{status, code, data:{...}}`, and `baseService.login`
      // hands back the parsed body untouched — so without this the caller received the envelope and
      // every field it looked for (`verification_required`, and formerly `access_token`) was
      // undefined one level up. Sibling calls here already unwrap (`verifyAuthToken`,
      // `resendVerificationCode` both return `response.data`); login was the odd one out.
      // The `?? response` keeps a bare, un-enveloped body working too.
      .pipe(map<any, SessionModel>(response => (response?.data ?? response) as SessionModel),
      tap((session: any) => {
        // Retained only while a two-factor step is pending (see pendingVerificationToken); a login
        // that needs no verification is already a complete cookie session.
        //
        // `access_token` is no longer in the body by design — it is set as an HttpOnly cookie — so
        // this settles to null and the two-factor call authenticates from that cookie.
        // `withPendingToken` passes the body through unchanged when it is null.
        this.pendingVerificationToken = session?.verification_required === Constants.VERIFICATION_REQUIRED
          ? (session?.access_token ?? null)
          : null;
      }),
      catchError(err => throwError(() => err))
  );
  }
  verifyAuthToken(verificationTokenModel: any): Observable<ResponseModel> {
    return this.baseService.post('auth/verify-code', this.withPendingToken(verificationTokenModel))
      .pipe(map<any, ResponseModel>(response => {
        return response.data;
      }),
      tap(() => {
        // Verified: the session now rests entirely on the HttpOnly cookie.
        this.pendingVerificationToken = null;
      }),
      catchError(err => throwError(() => err))
  );
  }
  getLoginUserDetail(): Observable<any> {
    return this.baseService.get('auth/me')
      .pipe(map<HttpResponse<any>, any>(response => {
        return response.body.data as any;
      }),
      catchError(err => throwError(() => err))
  );
  }
  resendVerificationCode(verificationTokenModel: any): Observable<ResponseModel> {
    return this.baseService.post('auth/resend-verification-code', this.withPendingToken(verificationTokenModel))
      .pipe(map<any, ResponseModel>(response => {
        return response.data;
      }),
      catchError(err => throwError(() => err))
  );
  }
  getSetting(): Observable<any> {
    return this.baseService.get('setting')
      .pipe(map<HttpResponse<any>, any>(response => {
        return response.body.data as any;
      }),
      catchError(err => throwError(() => err))
  );
  }
  getSettingViewDetail(): Observable<any> {
    return this.baseService.get('setting/view')
      .pipe(map<HttpResponse<any>, any>(response => {
        return response.body.data as any;
      }),
      catchError(err => throwError(() => err))
  );
  }

  forgotPassword(forgotPasswordModel: any): Observable<ResponseModel> {
    return this.baseService.post('auth/forgot-password', forgotPasswordModel)
      .pipe(map<any, ResponseModel>(response => {
        return response.data;
      }),
      catchError(err => throwError(() => err))
  );
  }

  changePassword(changePasswordModel: ChangePasswordModel): Observable<ResponseModel> {
    return this.baseService.post('auth/change-password', changePasswordModel)
      .pipe(map<any, ResponseModel>(response => {
        return response.data;
    }),
        catchError(err => throwError(() => err))
    );
  }
  verifyToken(resetPasswordModel: ResetPasswordModel): Observable<ResponseModel> {
    return this.baseService.getWithoutToken('adminauth/reset/' + resetPasswordModel.password_reset_token)
      .pipe(map<HttpResponse<any>, any>(response => {
        return response;
    }),
        catchError(err => throwError(() => err))
    );
  }
  /**
   * Is this reset link still usable? Read-only — it consumes nothing and returns the same verdict
   * the real reset would, so the screen can refuse a spent link before taking a new password.
   * Admin links are single-use and last an hour, so this path is hit often.
   */
  validateResetToken(clientId: string, token: string): Observable<any> {
    return this.baseService.post('auth/reset-password/validate',
      { client_id: clientId, password_reset_token: token }, true, true)
      .pipe(catchError(err => throwError(() => err)));
  }

  resetPassword(resetPasswordModel: ResetPasswordModel): Observable<ResponseModel> {
    return this.baseService.post('auth/reset-password', resetPasswordModel)
      .pipe(map<any, ResponseModel>(response => {
        return response.data;
    }),
        catchError(err => throwError(() => err))
    );
  }
  updateProfile(requestModel: UserModel): Observable<UserModel> {
    return this.baseService.patch('auth/profile', requestModel)
      .pipe(map<any, UserModel>(response => {
        return response.data;
    }),
        catchError(err => throwError(() => err))
    );
  }
  updateProfilePicture(requestModel: any): Observable<any> {
    return this.baseService.uploadPost('auth/profile-picture', requestModel)
      .pipe(map<any, any>(response => {
        return response.data;
    }),
        catchError(err => throwError(() => err))
    );
  }
  /**
   * Removes the logged-in user's profile picture (S3 object + column) so the avatar
   * falls back to their initials. Replacement does NOT need this call —
   * `auth/profile-picture` (POST) already deletes the previous file before storing the new one.
   */
  deleteProfilePicture(): Observable<any> {
    return this.baseService.delete('auth/profile-picture')
      .pipe(map<any, any>((response: any) => {
        return response?.data;
    }),
        catchError(err => throwError(() => err))
    );
  }
  setAdminDetail(detail: any) {
    let model: AdminUserModel = { id: detail.id, first_name: detail.first_name, last_name: detail.last_name, email: detail.email, role: detail.role, profile_picture: detail.profile_picture };
    // Held in memory only — no localStorage. currentUserValue / getAdminUser() read from here.
    this.currentUserSubject.next(model as any);
  }

  /**
   * Adds the in-memory access_token fallback to a two-factor request body. Omitted entirely when
   * there is nothing to send, so the server keeps reading the HttpOnly cookie as the primary
   * credential.
   */
  private withPendingToken(bodyModel: any): any {
    return this.pendingVerificationToken
      ? {...bodyModel, access_token: this.pendingVerificationToken}
      : bodyModel;
  }

  /**
   * THE single authentication-failure flow. Every caller that discovers the session is gone — the
   * 401 branch of {@link ErrorInterceptor} and the header's Logout button — funnels through here so
   * the teardown happens once and in one order: clear the client-side auth state, drop the
   * per-session localStorage leftovers, then route to /login.
   *
   * Two invariants keep it from looping:
   *
   *   1. It navigates with the Router and NEVER touches window.location. A document reload re-runs
   *      the APP_INITIALIZER /me probe; if that probe still succeeds (e.g. only the Node API
   *      rejected the session, or only the AI service token expired) LoginComponent bounces
   *      straight back to /dashboard, whose widgets 401 again and reload again — that was the
   *      observed infinite reload.
   *   2. It is a no-op while a teardown is already in flight, and on the public auth routes. The
   *      dashboard alone mounts ~15 widgets that all fail together; without the latch that was
   *      ~15 toasts, ~15 clearToken() calls and ~15 router.navigate() calls, each cancelling the
   *      previous navigation.
   *
   * @returns true when THIS call performed the teardown (the caller owns the user-facing message),
   *          false when one was already running or the user is already on a public auth page.
   */
  endSession(): boolean {
    if (this.endingSession || this.isOnPublicAuthPage()) {
      return false;
    }
    this.endingSession = true;
    this.clearToken();
    localStorage.removeItem('sampleOrder');
    // Released once the navigation has settled, so the rest of the 401 burst is swallowed while the
    // redirect is in flight; a genuinely new expiry later in the session is still handled (by then
    // we are on /login, where isOnPublicAuthPage() short-circuits anyway).
    this.router.navigate(['/login']).finally(() => {
      this.endingSession = false;
    });
    return true;
  }

  /** True while {@link endSession} is tearing the session down (used to stop further authenticated requests). */
  isSessionEnding(): boolean {
    return this.endingSession;
  }

  /**
   * True when the browser is on one of {@link PUBLIC_AUTH_PATHS}.
   *
   * Both sources are consulted on purpose: during bootstrap the Router has not navigated yet, so
   * `router.url` is still '/' and only `location.pathname` is meaningful; after bootstrap
   * `router.url` is the one that already reflects a redirect in flight.
   */
  private isOnPublicAuthPage(): boolean {
    return [location.pathname, this.router.url].some(url =>
      AuthService.PUBLIC_AUTH_PATHS.some(path => url === path || url.startsWith(path + '/') || url.startsWith(path + '?'))
    );
  }

  clearToken(): void {
    // Clear in-memory auth state; also purge any legacy localStorage values from older builds.
    this.currentUserSubject.next(null as any);
    this.serviceTokenService.clear();
    localStorage.removeItem('token');
    localStorage.removeItem('loader');
    localStorage.removeItem('admin_user');
    // Listing filters/search/sort/page remembered for this tab belong to this session only.
    clearListStates();
  }

  /**
   * Server-side logout: revoke the token behind the cookie and clear the cookies, then reset local
   * auth state regardless of the network result.
   *
   * skipError is deliberate. `/v3/oauth/logout` is an authenticated endpoint, so logging out on an
   * ALREADY expired session answers 401 — routed through the global handler that would make the
   * logout click trigger the session-expiry teardown as well as the caller's own, and pop a
   * "Your session has expired" toast on a perfectly ordinary logout. The outcome is irrelevant here:
   * either the token was revoked, or it was already worthless. The caller finishes the teardown via
   * {@link endSession}.
   */
  logout(): Observable<any> {
    return this.baseService.post('auth/logout', {}, false, true).pipe(
      catchError(() => of(null)),
      finalize(() => {
        // Drop any "come back here" marker. Nine screens set `redirect_url` before navigating
        // away, and only the sign-in paths consume it — so without this, deliberately signing out
        // from one screen sent the NEXT sign-in to wherever the marker happened to point.
        //
        // Deliberately NOT in clearToken(): that also runs when a session EXPIRES, and there the
        // marker is exactly what should survive so the person lands back where they were thrown
        // out of. Choosing to leave and being shown the door are different things.
        try { localStorage.removeItem('redirect_url'); } catch { /* storage unavailable */ }
        this.clearToken();
      })
    );
  }
  public ngOnDestroy(): void {
    this.currentUserSubject.unsubscribe();
  }
  //#endregion
}
//#endregion
